// Lot + buildable-envelope math for setback checks and the coverage meter.

// Estimate a lot from the building footprint (used until the user sets a real parcel).
export function defaultLot(rooms, margin = 15) {
  if (!rooms || !rooms.length) return { x: 6, y: 6, w: 60, h: 100 };
  const bx0 = Math.min(...rooms.map((r) => r.x));
  const by0 = Math.min(...rooms.map((r) => r.y));
  const bx1 = Math.max(...rooms.map((r) => r.x + r.w));
  const by1 = Math.max(...rooms.map((r) => r.y + r.h));
  return {
    x: Math.max(0, bx0 - margin),
    y: Math.max(0, by0 - margin),
    w: (bx1 - bx0) + margin * 2,
    h: (by1 - by0) + margin * 2,
  };
}

// Compute the buildable envelope (lot minus setbacks) and flag rooms that cross it.
// `lot` is an explicit {x,y,w,h} parcel when the user has one; otherwise it is estimated.
export function computeSetback(rooms, setbacks, lot = null, margin = 15) {
  if (!setbacks) return null;
  const rs = rooms || [];
  const L = lot || defaultLot(rs, margin);
  const front = Number(setbacks.front) || 0;
  const rear = Number(setbacks.rear) || 0;
  const side = Number(setbacks.side) || 0;
  const env = {
    x: L.x + side,
    y: L.y + front,
    w: Math.max(1, L.w - side * 2),
    h: Math.max(1, L.h - front - rear),
  };
  const violationIds = new Set();
  rs.forEach((r) => {
    if (r.x < env.x - 0.01 || r.y < env.y - 0.01 || r.x + r.w > env.x + env.w + 0.01 || r.y + r.h > env.y + env.h + 0.01) {
      violationIds.add(r.id);
    }
  });
  return { lot: L, env, violationIds, count: violationIds.size, front, rear, side };
}

// Live lot-coverage: building footprint (ground-floor rooms) as a % of parcel area.
export function computeCoverage(rooms, lot) {
  if (!lot || !lot.w || !lot.h) return null;
  const footprint = (rooms || []).reduce((s, r) => s + (r.w || 0) * (r.h || 0), 0);
  const lotArea = Math.max(lot.w * lot.h, 1);
  return { footprint: Math.round(footprint), lotArea: Math.round(lotArea), pct: footprint / lotArea * 100 };
}
